/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { getIn } from 'formik';
import type { AnyAction, Dispatch } from 'redux';

import { updateField } from '../../store/customer';
import { clearFieldError } from '../../store/fieldError';
import { contactDeliveryType } from '../../utils/addressHelpers';
import { NUMBER_FORMAT } from '../../utils/constants';
import { ErrorIcon, ErrorValidation } from '../ErrorValidation';

function addHyphens(
  value: string,
  dispatch: Dispatch<AnyAction>,
  name: string
) {
  if (
    value &&
    value.length === 10 &&
    /^\d{1,255}$/i.test(value)
  ) {
    const newNumber = value.replace(
      /(\d{3})(\d{3})(\d{4})/,
      '$1-$2-$3'
    );
    dispatch(updateField({ name, value: newNumber }));
  }
}

const handleError = (
  error: any,
  name: string,
  dispatch: Dispatch<AnyAction>
) => {
  if (getIn(error, name) === undefined) {
    dispatch(clearFieldError(name));
  }
};

const renderError = (
  hasError: boolean,
  isBlur: boolean,
  errorMessage: any
) =>
  hasError &&
  isBlur &&
  errorMessage && (
    <ErrorValidation>
      <ErrorIcon />
      <p>{errorMessage}</p>
    </ErrorValidation>
  );

interface FieldNumberFormatProps {
  formik: any;
  store: any;
  name: string;
  required?: boolean;
  label?: string;
  footer?: string;
  placeholder?: string;
  hasGroupError?: boolean;
  dispatch: Dispatch<AnyAction>;
  apiService: any;
  mask?: string;
  format?: string;
  onChange?: (args: {
    value: string;
    dispatch: Dispatch<AnyAction>;
    formik: any;
    apiService: any;
    store: any;
  }) => void;
  disableAutoSave?: boolean;
  isReview?: boolean;
  onBlur?: (args: {
    dispatch: Dispatch<AnyAction>;
    formik: any;
    apiService: any;
    store: any;
    error: any;
  }) => void;
}

function FieldNumberFormat({
  formik,
  store,
  name,
  required,
  label,
  footer,
  placeholder,
  hasGroupError,
  dispatch,
  apiService,
  mask,
  format,
  onChange,
  disableAutoSave,
  isReview,
  onBlur = () => {}
}: FieldNumberFormatProps): JSX.Element {
  const [isDirty, setFieldDirty] = useState(false);
  const [isBlur, setIsBlur] = useState(false);

  useEffect(() => {
    const { value } = formik.field;
    addHyphens(value, dispatch, name);
  }, [dispatch, formik.field, name]);

  const handleOnBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setIsBlur(true);

    if (isDirty) {
      contactDeliveryType(name, dispatch);
      dispatch(updateField({ name, value }));

      formik.form
        .setFieldTouched(name, true)
        .then((error: any) => {
          onBlur({ dispatch, formik, apiService, store, error });
          handleError(error, name, dispatch);

          !disableAutoSave &&
            !hasError &&
            apiService.updateCustomer(
              store.customer,
              dispatch
            );
        });

      setFieldDirty(false);
    }
  };

  const errorMessage = getIn(formik.form.errors, name);
  const hasError = errorMessage !== undefined;

  const handleOnChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setFieldDirty(true);
    formik.form.setFieldValue(name, value);

    onChange?.({
      value,
      dispatch,
      formik,
      apiService,
      store
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        mb: '16px',
        paddingTop: '4px'
      }}
    >
      <NumberFormat
        customInput={TextField}
        variant="outlined"
        fullWidth
        label={label || ''}
        required={!!required}
        inputProps={{ 'aria-required': !!required }}
        error={
          (hasError && (isReview || isBlur)) ||
          hasGroupError
        }
        format={format || NUMBER_FORMAT.telephone}
        mask={mask || '_'}
        name={name}
        onBlur={handleOnBlur}
        value={formik.field.value || ''}
        placeholder={placeholder}
        onChange={handleOnChange}
      />

      {renderError(hasError, isBlur, errorMessage)}

      {footer && (
        <Box
          sx={{
            mt: '3px',
            fontSize: '12px',
            fontWeight: 300,
            '& a': { color: 'var(--cargill-green)' }
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}

export default FieldNumberFormat;
